/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState } from 'react'
import { aiAssistantAxios } from '../../utils/io/http/axios'
import './AIAnalytics.scss'
import { renderSchema } from './AIAnalytics'

// ─── Types ────────────────────────────────────────────────────────────────

interface AppDetails {
  id: string
  app_name: string
  organization_name: string | null
}

interface ConversationMessage {
  id: string
  conversationId: string
  role: 'user' | 'assistant'
  message: string
  statuses: string | null
  error: string | null
  action: string | null
  schema: SchemaDisplay | null
  createdAt: string
}

interface SchemaDisplay {
  [key: string]: unknown
}

interface AppConversation {
  id: string
  type: string
  userId: number
  createdAt: string
  user_email: string
  user_name: string
  message_count: number
  user_messages: number
  assistant_messages: number
  error_count: number
  feedback_rating: 'thumbs_up' | 'thumbs_down' | null
}

interface AppConversationsData {
  data: AppConversation[]
  pagination: { offset: number; limit: number; total: number }
}

interface ConversationFeedbackInfo {
  rating: 'thumbs_up' | 'thumbs_down'
  feedback: string | null
  createdAt: string
}

interface StatusDisplay {
  type: 'success' | 'error' | 'info'
  message: string
}

// ─── Component ────────────────────────────────────────────────────────────────

const CONVERSATIONS_PAGE_SIZE = 20

export const AIAppConversationsSearch: React.FC = () => {
  const [appId, setAppId] = useState<string>('')
  const [appDetails, setAppDetails] = useState<AppDetails | null>(null)
  const [conversations, setConversations] =
    useState<AppConversationsData | null>(null)
  const [conversationPage, setConversationPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Expanded conversation messages
  const [expandedConversation, setExpandedConversation] = useState<
    string | null
  >(null)
  const [conversationMessages, setConversationMessages] = useState<
    Record<string, ConversationMessage[]>
  >({})
  const [conversationFeedback, setConversationFeedback] = useState<
    Record<string, ConversationFeedbackInfo | null>
  >({})
  const [loadingConversation, setLoadingConversation] = useState<string | null>(
    null
  )

  const fetchConversations = async (targetAppId: string, offset: number) => {
    try {
      const { data } = await aiAssistantAxios.get<AppConversationsData>(
        `/admin/ai/app/${targetAppId}/conversations?offset=${offset}&limit=${CONVERSATIONS_PAGE_SIZE}`
      )
      setConversations(data)
    } catch (err) {
      console.error('Failed to fetch conversations', err)
      setError('Failed to fetch conversations')
    }
  }

  const handleSearch = async () => {
    const AppId = appId.trim()
    if (!AppId) {
      setError('Please enter an app ID')

      return
    }

    setLoading(true)
    setError(null)
    setAppDetails(null)
    setConversations(null)
    setConversationPage(1)
    setExpandedConversation(null)
    setConversationMessages({})
    setConversationFeedback({})

    try {
      // Fetch app details
      const detailsResponse = await aiAssistantAxios.get<AppDetails>(
        `/admin/ai/app/${AppId}/details`
      )
      setAppDetails(detailsResponse.data)

      // Fetch conversations
      await fetchConversations(AppId, 0)
    } catch (err: unknown) {
      console.error('Failed to fetch app details', err)
      const axiosErr = err as { response?: { data?: { error?: string } } }
      setError(
        axiosErr.response?.data?.error ||
          'Failed to fetch app details. Please check the app ID.'
      )
      setAppDetails(null)
    } finally {
      setLoading(false)
    }
  }

  const handleConversationPageChange = (page: number) => {
    if (!appId.trim() || !appDetails) return
    const offset = (page - 1) * CONVERSATIONS_PAGE_SIZE
    setConversationPage(page)
    fetchConversations(appId.trim(), offset)
  }

  const handleToggleConversation = async (conversationId: string) => {
    if (expandedConversation === conversationId) {
      setExpandedConversation(null)

      return
    }

    setExpandedConversation(conversationId)

    // Already loaded
    if (conversationMessages[conversationId]) return

    setLoadingConversation(conversationId)
    try {
      const { data } = await aiAssistantAxios.get<{
        messages: ConversationMessage[]
        feedback: ConversationFeedbackInfo | null
      }>(`/admin/ai/conversation/${conversationId}/messages`)
      setConversationMessages(prev => ({
        ...prev,
        [conversationId]: data.messages.map(m => ({
          ...m,
          ...(m.schema !== null && m.schema !== undefined
            ? { schema: JSON.parse(m.schema as unknown as string) }
            : {}),
        })),
      }))
      setConversationFeedback(prev => ({
        ...prev,
        [conversationId]: data.feedback,
      }))
    } catch (err) {
      console.error('Failed to fetch conversation messages', err)
    } finally {
      setLoadingConversation(null)
    }
  }

  const parseStatuses = (statusesRaw: string | null): StatusDisplay[] => {
    if (!statusesRaw) return []
    try {
      const parsed = JSON.parse(statusesRaw)
      if (Array.isArray(parsed)) {
        return parsed.map((s: { type?: string; message?: string }) => ({
          type: s.type || 'info',
          message: s.message || String(s),
        }))
      }

      return []
    } catch {
      return []
    }
  }

  const getResponseTimeSeconds = (
    messages: ConversationMessage[],
    idx: number
  ): number | null => {
    const msg = messages[idx]
    if (!msg || msg.role !== 'assistant') return null

    const prevMsg = messages[idx - 1]
    if (!prevMsg || prevMsg.role !== 'user') return null

    const userTime = new Date(prevMsg.createdAt).getTime()
    const assistantTime = new Date(msg.createdAt).getTime()

    return (assistantTime - userTime) / 1000
  }

  const getResponseTimeClass = (seconds: number) => {
    if (seconds > 60) return 'slow'
    if (seconds > 30) return 'medium'

    return 'fast'
  }

  const formatResponseTime = (seconds: number): string => {
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`

    return `${seconds.toFixed(0)}s`
  }

  const conversationsTotalPages = conversations?.pagination
    ? Math.ceil(conversations.pagination.total / CONVERSATIONS_PAGE_SIZE)
    : 0

  return (
    <div className="ai-analytics-page">
      <div className="ai-analytics-header" style={{ flexDirection: 'column' }}>
        <h1>Search App Conversations</h1>

        <div className="analytics-section">
          <div style={{ marginBottom: '20px' }}>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <input
                type="text"
                value={appId}
                onChange={e => setAppId(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
                placeholder="Enter App ID"
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  minWidth: '300px',
                }}
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="export-button"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{ color: '#dc2626', marginBottom: '16px', fontSize: '14px' }}
          >
            {error}
          </div>
        )}

        {appDetails && (
          <div className="stats-flex" style={{ marginBottom: '24px' }}>
            <div className="stat-card">
              <div className="stat-label">Application Name</div>
              <div className="stat-value" style={{ fontSize: '20px' }}>
                {appDetails.app_name || '—'}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Organization</div>
              <div className="stat-value" style={{ fontSize: '20px' }}>
                {appDetails.organization_name || '—'}
              </div>
            </div>
          </div>
        )}
      </div>

      {conversations && (
        <table style={{ width: '100%' }}>
          <tr className="expanded-row">
            <td colSpan={4} style={{ padding: 0 }}>
              <div className="app-conversations-panel">
                {loading && (
                  <div className="conversation-loading">
                    Loading conversations...
                  </div>
                )}
                {conversations &&
                  conversations.data.map(conv => {
                    const isConvExpanded = expandedConversation === conv.id
                    const msgs = conversationMessages[conv.id]
                    const isMsgLoading = loadingConversation === conv.id

                    return (
                      <div
                        key={conv.id}
                        className={`app-conversation-item ${
                          isConvExpanded ? 'expanded' : ''
                        }`}
                      >
                        <div
                          className="app-conversation-header"
                          onClick={e => {
                            e.stopPropagation()
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            handleToggleConversation(conv.id)
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation()
                              // eslint-disable-next-line @typescript-eslint/no-floating-promises
                              handleToggleConversation(conv.id)
                            }
                          }}
                        >
                          <span
                            className={`row-expand-icon small ${
                              isConvExpanded ? 'open' : ''
                            }`}
                          >
                            &#9662;
                          </span>
                          <div className="app-conversation-info">
                            <span className="app-conversation-user">
                              {conv.user_name || 'Unknown'}{' '}
                              <span className="app-conversation-email">
                                ({conv.user_email || '—'})
                              </span>
                            </span>
                            <span className="app-conversation-stats">
                              {conv.message_count} msgs
                              {conv.error_count > 0 && (
                                <span className="app-conversation-errors">
                                  {conv.error_count} errors
                                </span>
                              )}
                              {conv.feedback_rating && (
                                <span
                                  className={`app-conversation-feedback ${conv.feedback_rating}`}
                                >
                                  {conv.feedback_rating === 'thumbs_up'
                                    ? '👍'
                                    : '👎'}
                                </span>
                              )}
                            </span>
                          </div>
                          <span className="app-conversation-date">
                            {new Date(conv.createdAt).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              }
                            )}
                          </span>
                        </div>

                        {isConvExpanded && (
                          <div className="conversation-thread">
                            {isMsgLoading && (
                              <div className="conversation-loading">
                                Loading messages...
                              </div>
                            )}
                            {msgs && msgs.length > 0 && (
                              <div className="conversation-messages">
                                <a
                                  href={`/admin/ai/conversation/${conv.id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="primary-link"
                                >
                                  Go to Conversation
                                </a>
                                {msgs.map((msg, idx) => {
                                  const statuses = parseStatuses(msg.statuses)
                                  const responseSeconds =
                                    getResponseTimeSeconds(msgs, idx)

                                  return (
                                    <div
                                      key={msg.id}
                                      className={`conversation-msg ${msg.role}`}
                                    >
                                      <div className="conversation-msg-header">
                                        <span
                                          className={`conversation-msg-role ${msg.role}`}
                                        >
                                          {msg.role === 'user'
                                            ? 'User'
                                            : 'AI Assistant'}
                                        </span>
                                        <span className="conversation-msg-time">
                                          {new Date(
                                            msg.createdAt
                                          ).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}
                                        </span>
                                        {responseSeconds !== null && (
                                          <span
                                            className={`conversation-msg-response-time ${getResponseTimeClass(
                                              responseSeconds
                                            )}`}
                                          >
                                            Response:{' '}
                                            {formatResponseTime(
                                              responseSeconds
                                            )}
                                          </span>
                                        )}
                                        {msg.error && (
                                          <span className="conversation-msg-error-badge">
                                            Error
                                          </span>
                                        )}
                                      </div>
                                      <div className="conversation-msg-body">
                                        {msg.message}
                                      </div>
                                      {msg.schema !== null &&
                                        msg.schema !== undefined &&
                                        renderSchema(
                                          msg as unknown as Parameters<
                                            typeof renderSchema
                                          >[0]
                                        )}
                                      {statuses.length > 0 && (
                                        <div className="conversation-msg-statuses">
                                          {statuses.map(s => (
                                            <span
                                              key={`${msg.id}-${
                                                s.type
                                              }-${s.message.slice(0, 20)}`}
                                              className={`conversation-status-badge status-${s.type}`}
                                            >
                                              {s.type === 'success' && '✓ '}
                                              {s.type === 'error' && '✕ '}
                                              {s.type === 'info' && 'ℹ '}
                                              {s.message}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                      {msg.error && (
                                        <div className="conversation-msg-error">
                                          {msg.error}
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                            {conversationFeedback[conv.id] && (
                              <div
                                className={`conversation-feedback-banner ${
                                  conversationFeedback[conv.id]?.rating ?? ''
                                }`}
                              >
                                <span className="conversation-feedback-icon">
                                  {conversationFeedback[conv.id]?.rating ===
                                  'thumbs_up'
                                    ? '👍'
                                    : '👎'}
                                </span>
                                <div className="conversation-feedback-detail">
                                  <span className="conversation-feedback-label">
                                    User rated this{' '}
                                    {conversationFeedback[conv.id]?.rating ===
                                    'thumbs_up'
                                      ? 'helpful'
                                      : 'not helpful'}
                                  </span>
                                  {conversationFeedback[conv.id]?.feedback && (
                                    <span className="conversation-feedback-text">
                                      &quot;
                                      {conversationFeedback[conv.id]?.feedback}
                                      &quot;
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            {msgs && msgs.length === 0 && (
                              <div className="conversation-loading">
                                No messages found.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                {conversations && conversations.data.length === 0 && (
                  <div className="conversation-loading">
                    No conversations found for this app.
                  </div>
                )}
              </div>
            </td>
          </tr>
        </table>
      )}

      {conversationsTotalPages !== undefined && conversationsTotalPages > 1 && (
        <div className="ai-pagination">
          <div className="pagination-info">
            Page {conversationPage} of {conversationsTotalPages} (
            {conversations?.pagination.total} total)
          </div>
          <div className="pagination-controls">
            <button
              type="button"
              disabled={conversationPage === 1 || loading}
              onClick={() => handleConversationPageChange(1)}
            >
              First
            </button>
            <button
              type="button"
              disabled={conversationPage === 1 || loading}
              onClick={() => handleConversationPageChange(conversationPage - 1)}
            >
              Prev
            </button>
            <span className="page-info">
              {conversationPage} / {conversationsTotalPages}
            </span>
            <button
              type="button"
              disabled={conversationPage === conversationsTotalPages || loading}
              onClick={() => handleConversationPageChange(conversationPage + 1)}
            >
              Next
            </button>
            <button
              type="button"
              disabled={conversationPage === conversationsTotalPages || loading}
              onClick={() => handleConversationPageChange(conversationPage)}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
